/*
 * VIVO LUXE — scroll reveals, header state, heading hairlines, variant photo crossfade.
 * Loaded (deferred) by snippets/vivo-luxe.liquid. No dependencies.
 */
(() => {
  const root = document.documentElement;
  window.vivoLuxe = true;

  /* Header: frosted state once the page scrolls. Horizon scrolls inside .page-wrapper, not the
     window, and element scroll events don't bubble — so listen in the capture phase. */
  const scroller = document.querySelector('.page-wrapper');
  let ticking = false;
  const updateHeader = () => {
    const offset = scroller ? scroller.scrollTop : window.scrollY;
    root.classList.toggle('vivo-scrolled', offset > 24 || window.scrollY > 24);
    ticking = false;
  };
  document.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateHeader);
      }
    },
    { capture: true, passive: true }
  );
  updateHeader();

  /* Gold hairline under section headings, aligned like the heading itself. */
  const markRules = (scope) => {
    scope
      .querySelectorAll(
        'main .text-block:is(.h1, .h2, .h3) > :is(h1, h2, h3):not(.vivo-rule, product-card *, .product-information *)'
      )
      .forEach((heading) => {
        heading.classList.add('vivo-rule');
        if (getComputedStyle(heading).textAlign === 'center') heading.classList.add('vivo-rule--center');
      });
  };

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Product page: when a variant (metal, carat) swaps a photo, the new photo fades in once it has
     loaded. Horizon swaps src on the same <img>, so watch attributes rather than a CSS class. */
  const fadeIn = (img) => {
    if (!img.getBoundingClientRect().width) return;
    const run = () =>
      img.animate([{ opacity: 0.2, transform: 'scale(1.012)' }, { opacity: 1, transform: 'none' }], {
        duration: 800,
        easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      });
    if (img.complete) run();
    else img.addEventListener('load', run, { once: true });
  };

  if (!reducedMotion && 'animate' in Element.prototype) {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
            fadeIn(mutation.target);
          }
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLImageElement) fadeIn(node);
            else if (node instanceof Element) node.querySelectorAll('img').forEach(fadeIn);
          });
        }
      }).observe(gallery, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    });
  }

  /* Scroll reveals. Keep REVEAL in sync with the hidden-state rule in assets/vivo-luxe.css. */
  const REVEAL =
    'main > .shopify-section:not(:first-child) :is(.text-block, .section-content-wrapper > a, product-card, .collection-card):not(product-card *, marquee-component *)';

  const motionOn = root.classList.contains('vivo-motion') && 'IntersectionObserver' in window && !reducedMotion;

  // Without reveals nothing may stay in its hidden pre-reveal state.
  if (!motionOn) root.classList.remove('vivo-motion');

  let observer;
  if (motionOn) {
    observer = new IntersectionObserver(
      (entries) => {
        let index = 0;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          // Items arriving together (a row of cards) cascade in gently.
          entry.target.style.setProperty('--vivo-delay', `${Math.min(index++ * 90, 450)}ms`);
          entry.target.classList.add('vivo-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );
  }

  const scan = (scope = document) => {
    markRules(scope);
    if (!observer) return;
    let targets;
    try {
      targets = scope.querySelectorAll(REVEAL);
    } catch {
      root.classList.remove('vivo-motion');
      return;
    }
    targets.forEach((element) => {
      if (element.hasAttribute('data-vivo-reveal')) return;
      element.setAttribute('data-vivo-reveal', '');
      observer.observe(element);
    });
  };

  scan();

  // Filters, pagination and recommendations re-render parts of the page; pick up new nodes.
  const main = document.getElementById('MainContent');
  if (main) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        scan();
      });
    }).observe(main, { childList: true, subtree: true });
  }
})();
